import { Field, Form, Formik, useField } from 'formik';
import { GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Input } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import AutoSave from 'packages/kakapo-ui-kit/components/forms/AutoSave';
import FriendlyMessage from 'packages/kakapo-ui-kit/components/forms/errors/FriendlyMessage';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import UserCollaboratorSelector from 'packages/kakapo-ui-kit/components/users/UserCollaboratorsSelector';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';

const PageTitleField = styled(Field)`
	width: 100%;
	display: block;
	overflow: hidden;
	font-size: 22px;
	font-weight: 700;
	color: ${(props) => props.theme.colors.antracite};
	background-color: transparent;

	:disabled {
		background-color: transparent;
	}
`;

const NoteDateField = ({ name, disabled }: { name: string; disabled?: boolean }) => {
	const [field, , helpers] = useField<Date>(name);

	return (
		<Flex>
			<Input.DatePicker
				onChange={(date) => {
					helpers.setValue(date ?? new Date());
				}}
				value={field.value}
				disabled={disabled}
			/>
		</Flex>
	);
};

interface INoteMetaEditorValues {
	title: string;
	date: Date;
}

const NotesMetaEditContainer = ({ note }: { note: graphql.Note }) => {
	const { t } = useTranslation();
	const [error, setError] = useState<GraphQLError | undefined>(undefined);
	const [share] = graphql.useShareNoteMutation();
	const [unshare] = graphql.useUnshareNoteMutation();
	const [update] = graphql.useUpdateNoteMutation();
	const authenticatedUser = useAuthenticatedUser();

	const updateNote = async (values: INoteMetaEditorValues) => {
		const date = [
			values.date.getFullYear().toFixed().padStart(4, '0'),
			(values.date.getMonth() + 1).toFixed().padStart(2, '0'),
			values.date.getDate().toFixed().padStart(2, '0'),
		].join('-');

		if (note.date === date && note.title === values.title) {
			return;
		}

		setError(undefined);

		try {
			await update({
				variables: {
					input: {
						noteID: note.id,
						title: values.title,
						date,
					},
				},
			});
		} catch (e) {
			setError(e.graphQLErrors[0]);
		}
	};

	const shareNote = async (user: graphql.User) => {
		setError(undefined);

		try {
			await share({
				variables: {
					input: {
						noteID: note.id,
						userID: user.id!,
					},
				},
			});
		} catch (e) {
			setError(e.graphQLErrors[0]);
		}
	};

	const unshareNote = async (user: graphql.User) => {
		setError(undefined);

		try {
			await unshare({
				variables: {
					input: {
						noteID: note.id,
						userID: user.id!,
					},
				},
			});
		} catch (e) {
			setError(e.graphQLErrors[0]);
		}
	};

	const collaborators: graphql.User[] = note.collaborators.edges
		.map(({ node }) => node)
		.sort((a, b) => {
			if (a.id === note.owner.id) {
				return -1;
			}

			if (b.id === note.owner.id) {
				return 1;
			}

			return Math.sign(a.normalizedName.localeCompare(b.normalizedName));
		});

	const isAuthenticatedUserOwner = note.owner.id === authenticatedUser.id;

	return (
		<>
			<Formik
				key={note.id}
				onSubmit={(values) => {
					updateNote(values);
				}}
				initialValues={{
					title: note.title ?? '',
					date: note.date ? new Date(note.date) : new Date(),
				}}
				validationSchema={validator
					.object({
						title: validator.string().required(),
						date: validator.string().required(),
					})
					.required()}
			>
				<Form>
					{!isAuthenticatedUserOwner && (
						<FriendlyMessage>
							{t('notes.collaborators.sharedIndicationText', {
								ownerFirstName: note.owner.firstName,
							})}
						</FriendlyMessage>
					)}

					{isAuthenticatedUserOwner && <AutoSave debounceMs={500} />}

					<GraphQLErrorMessage error={error} />

					<Box mt={3}>
						<NoteDateField name="date" disabled={!isAuthenticatedUserOwner} />
					</Box>

					<Box mt={4} mb={3}>
						<PageTitleField
							type="text"
							placeholder={t('notes.title.placeholder')}
							name="title"
							autoComplete="off"
							disabled={!isAuthenticatedUserOwner}
						/>
					</Box>
				</Form>
			</Formik>

			<Box mb={4}>
				<UserCollaboratorSelector
					owner={note.owner}
					collaborators={collaborators}
					onShare={(user) => shareNote(user)}
					onUnshare={(user) => unshareNote(user)}
					disabled={!isAuthenticatedUserOwner}
				/>
			</Box>
		</>
	);
};

export default NotesMetaEditContainer;
